import { makeAutoObservable, runInAction } from 'mobx';
import currentPortalStore from "../../../stores/current-portal-store";
import ErrorReportingService from '../../../services/error-reporting.service';
import { rootContainer, TYPE } from '../../../services/container/root';
import CreateRmaResponseStore from "./create-rma-response-store";
import OrderStore from "./order-store";
import CreateRmaSettingsStore from './create-rma-settings-store';
import { CreateRmaOrderLookupStore } from "./create-rma-order-lookup-store";
import { CreateRmaDraftStore } from "./create-rma-draft-store";
import CreateRmaSessionStore from "./create-rma-session-store";
import { CreateRmaSubmissionStore } from "./create-rma-submission-store";
import {
    type IFulfillmentBlockingReason,
    type ICreateRmaOutcome,
} from "./create-rma-submission-types";


export class CreateRmaMainStore {
	private _lookup: CreateRmaOrderLookupStore;
	private _drafts: CreateRmaDraftStore[] = [];
	private _session?: CreateRmaSessionStore;
	private _submission: CreateRmaSubmissionStore;
	private _showResponseModal: boolean = false;
	private _createRmaResponseStore?: CreateRmaResponseStore;

	public get lookup(): CreateRmaOrderLookupStore { return this._lookup; }
	public get drafts(): CreateRmaDraftStore[] { return this._drafts; }
	public get isMixedOrder(): boolean { return this._lookup.isMixedOrder; }
	/** Major member order (group contract: orders[0]); used by the Order details card (FR15). */
	public get majorOrderStore(): OrderStore | undefined { return this._lookup.majorOrder; }
	/** FR7: drafts that own return-details, shipping and summary sub-blocks (includes created RMAs). */
	public get draftsWithSelectedItems(): CreateRmaDraftStore[] {
		return this._drafts.filter((draft) => draft.hasSelectedItems);
	}
	/** Drafts eligible for submission: selected items and not yet created. */
	public get submittableDrafts(): CreateRmaDraftStore[] {
		return this._drafts.filter((draft) => draft.hasSelectedItems && !draft.isRMACreated);
	}
	/** fees, automations and the internal note: configured once, applied to every RMA (FR7a) */
	public get session(): CreateRmaSessionStore | undefined { return this._session; }

	/** Per-fulfillment store-derived gates; item-level validation stays on validationGroup.isValid(). */
	public get blockingReasons(): IFulfillmentBlockingReason[] {
		return this.submittableDrafts
			.map((draft) => draft.getBlockingReason())
			.filter((reason): reason is IFulfillmentBlockingReason => reason !== null);
	}

	public get canSubmit(): boolean {
		return this.submittableDrafts.length > 0
			&& this.submittableDrafts.every((draft) => draft.isValid())
			&& !this._submission.isSubmitting;
	}

	public get isSearching(): boolean {
		return this._lookup.isSearching
			|| this._settingsStore.isLoadingDefaults
			|| this._orderStore?.customer?.isLoading
			|| !!this._orderStore?.customer?.isLoadingRmas;
	}
	public get searchError(): string | null { return this._lookup.searchError; }
	public get searchOrderName(): string { return this._lookup.searchOrderName; };
	public get orderStore(): OrderStore | undefined { return this._lookup.orders[0]; }
	public get settings(): CreateRmaSettingsStore { return this._settingsStore; }
	public get showResponseModal(): boolean { return this._showResponseModal; }
	public get createRmaResponseStore(): CreateRmaResponseStore | undefined { return this._createRmaResponseStore; }
	public get isCreatingRma(): boolean { return this._submission.isSubmitting; }

	public setSearchOrderName(value: string) {
		this._lookup.setSearchOrderName(value);
	}

	constructor(private _settingsStore: CreateRmaSettingsStore) {
		makeAutoObservable(this, {}, { autoBind: true });
		this._lookup = new CreateRmaOrderLookupStore();
		this._submission = new CreateRmaSubmissionStore(this._settingsStore);
	}

	public createRmaRequestStore(): void {
		this._buildDrafts(this._lookup.orders);
	}

	public __destroy():void {
		this._destroyDrafts();
	}

	private get _orderStore(): OrderStore | undefined {
		return this._lookup.orders[0];
	}

	private _destroyDrafts(): void {
		this._drafts.forEach((draft) => draft.__destroy());
		this._drafts = [];
		this._session = undefined;
	}

	/**
	 * Rebuilds the whole session: one session store for the page-level settings, then one draft per
	 * member order sharing it. Fee defaults depend on the mode, so a mode switch rebuilds both.
	 */
	private _buildDrafts(orders: OrderStore[]): void {
		this._destroyDrafts();
		if (orders.length === 0) {
			return;
		}

		const session = new CreateRmaSessionStore(this._settingsStore);
		this._session = session;
		this._drafts = orders.map(
			(orderStore, position) => new CreateRmaDraftStore(
				orderStore,
				position + 1,
				this._settingsStore,
				session,
			),
		);
	}

	public async searchForOrder() {
		/** when searching for order - destroy previous drafts to prevent intermediate state from the previous order still on the screen */
		this._destroyDrafts();

		const orders = await this._lookup.fetchOrder();
		if (orders.length === 0) {
			return;
		}

		const majorOrder = orders[0];
		try {
			await this._settingsStore.loadDefaultsForOrder({
				orderName: majorOrder.orderName,
			});
		} catch {
			this._lookup.setSearchError('Failed to load Create RMA defaults for this order');
			return;
		}

		runInAction(() => {
			majorOrder.loadCustomer();
			this._buildDrafts(orders);
		});
	}

	public async submit(): Promise<void> {
		if (!this.canSubmit) {
			return;
		}

		const drafts = this.submittableDrafts;
		const outcomes = await this._submission.run(drafts);
		if (outcomes.length !== drafts.length) {
			this._reportSubmissionOutcomeMismatch(drafts.length, outcomes.length);
		}
		this._openResponseModalFromOutcomes(outcomes);
	}

	private _reportSubmissionOutcomeMismatch(expectedCount: number, actualCount: number): void {
		try {
			const errorReportingService = rootContainer.get<ErrorReportingService>(TYPE.ErrorReportingService);
			errorReportingService.captureException(
				new Error('create-rma submission outcome count mismatch'),
				{ expectedCount, actualCount },
			);
		} catch (error) {
			console.error(error);
		}
	}

	private _openResponseModalFromOutcomes(outcomes: ICreateRmaOutcome[]): void {
		if (outcomes.length === 0) {
			return;
		}

		runInAction(() => {
			this._createRmaResponseStore = new CreateRmaResponseStore(outcomes);
			this._showResponseModal = true;
		});
	}

	public hideResponseModal() {
		this._showResponseModal = false;
	}

}


export class CreateRmaMainStoreInstanceManager {
	private _instance?: CreateRmaMainStore;
    private _settingsStore: CreateRmaSettingsStore = new CreateRmaSettingsStore(this);

    constructor() {
        makeAutoObservable(this);
    }

	public createInstance(): void {
        runInAction(() => {
			/** always destroy previous store if exists before instantiating a new one */
			this._instance?.__destroy()
			this._instance = new CreateRmaMainStore(this._settingsStore);
        });
	}

    public getInstance(): CreateRmaMainStore|undefined {
		return this._instance;
	}

    async loadSettings() {
		this._settingsStore.load();
		currentPortalStore.load();
    }

	public voidInstance(): void {
        runInAction(() => {
			this._instance?.__destroy()
            this._instance = undefined;
        });
	}
}

const instanceManager =  new CreateRmaMainStoreInstanceManager();
export default instanceManager;
